import { compareNumericStrings } from '@racemap/utilities/functions/compareNumericStrings';
import uniq from 'lodash/uniq';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import type { StarterType } from 'src/lib/player/store';
import { SortKey, type StarterSorting } from './types';

// `\w` is ASCII-only, so it would drop umlauts and every other non-ASCII letter.
const SEARCH_WORD_PATTERN = /[\p{L}\p{N}_]+/gu;

/**
 * Folds a string into its diacritic-free lower case form, so that "Höpt", "höpt"
 * and "hopt" all reduce to the same thing. `ß` folds to `ss` because NFD leaves
 * it untouched.
 */
export function foldSearchText(text: string): string {
  return text
    .toLowerCase()
    .replaceAll('ß', 'ss')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Builds a predicate that accepts a candidate containing any word of `searchText`.
 * A search without letters or digits accepts everything, like an empty search.
 */
export function createSearchMatcher(
  searchText: string,
): (candidate: string | null | undefined) => boolean {
  const words = uniq(foldSearchText(searchText).match(SEARCH_WORD_PATTERN) ?? []);
  if (words.length === 0) return () => true;

  return (candidate) => {
    if (candidate == null) return false;
    const folded = foldSearchText(candidate);
    return words.some((word) => folded.includes(word));
  };
}

/**
 * Toggles a tag value in the selected tags map.
 * If the value is currently selected, it will be removed.
 * If the value is not selected, it will be added.
 * If removing a value leaves no values for that tag key, the key is deleted.
 *
 * @param selectedTags - Current map of selected tags
 * @param tagKey - The tag key to toggle
 * @param value - The value to toggle
 * @returns A new Map with the toggled state (immutable)
 */
export function toggleSelectedTag(
  selectedTags: Map<string, Array<string>>,
  tagKey: string,
  value: string,
): Map<string, Array<string>> {
  const newSelectedTags = new Map(selectedTags);
  const currentValues = newSelectedTags.get(tagKey) || [];

  if (currentValues.includes(value)) {
    const filtered = currentValues.filter((v) => v !== value);
    if (filtered.length === 0) {
      newSelectedTags.delete(tagKey);
    } else {
      newSelectedTags.set(tagKey, filtered);
    }
  } else {
    newSelectedTags.set(tagKey, [...currentValues, value]);
  }

  return newSelectedTags;
}

export function deduplicateStarters(starters: Array<StarterType>): Array<StarterType> {
  const set: Set<string> = new Set();
  return starters.filter((starter) => {
    if (!set.has(starter.id)) {
      set.add(starter.id);
      return true;
    }
    return false;
  });
}

export function sortStarters(
  starters: Array<StarterType>,
  starterSorting: StarterSorting | null,
  renderInfoBuffer: RenderInfoBuffer,
): Array<StarterType> {
  if (starterSorting == null) return starters.slice();

  const sign = starterSorting[1] === 'ASC' ? 1 : -1;
  let sortFunc: (a: StarterType, b: StarterType) => number;
  switch (starterSorting[0]) {
    case SortKey.START_NUMBER:
      sortFunc = (a, b) => {
        if (a.startNumber == null && b.startNumber == null) return 0;
        if (a.startNumber == null) return 1 * sign;
        if (b.startNumber == null) return -1 * sign;
        return sign * compareNumericStrings(a.startNumber, b.startNumber);
      };
      break;
    case SortKey.NAME:
      sortFunc = (a, b) => {
        if (a.name == null && b.name == null) return 0;
        if (a.name == null) return 1 * sign;
        if (b.name == null) return -1 * sign;
        return sign * a.name.localeCompare(b.name);
      };
      break;
    case SortKey.OFFSET:
      sortFunc = (a, b) => {
        const renderIndexA = renderInfoBuffer.getRenderIndex(a.eventIndex, a.index);
        const renderIndexB = renderInfoBuffer.getRenderIndex(b.eventIndex, b.index);
        const offsetA = renderInfoBuffer.getOffset(renderIndexA);
        const offsetB = renderInfoBuffer.getOffset(renderIndexB);
        return sign * (offsetA - offsetB) || renderIndexA - renderIndexB;
      };
      break;
    default:
      console.warn('Unknown sort key:', starterSorting[0], 'Defaulting to no sorting.');
      return starters.slice();
  }
  return starters.slice().sort(sortFunc);
}
